/**
 * Pending-batch queue store (Zustand).
 *
 * Holds BatchResults returned by /api/sb/ingest for the current session.
 * SmartBox's BatchQueue/MemoryStore is transient server-side state: only
 * confirmed facts are durable. The client mirrors that: this store is an
 * in-memory session store. It survives navigation within the session but
 * is not persisted to disk (durable offline persistence is WO-M3).
 *
 * Pending files are those with status === 'pending'. Once a file is
 * confirmed or discarded, it is removed from the store via the update
 * helpers so the review queue shows only actionable items.
 */

import { create } from 'zustand';
import type { BatchResult, FileResult, CommitResult } from '@beam/api-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A pending file identified by its batch context. */
export interface PendingFile {
  batchId: string;
  fileResult: FileResult;
}

interface QueueState {
  /** All BatchResults ingested this session, keyed by batchId. */
  batches: Record<string, BatchResult>;

  /**
   * Add a BatchResult from a successful ingest call.
   * Only files with status 'pending' are actionable in the review queue.
   */
  addBatch: (batch: BatchResult) => void;

  /**
   * Mark a file as confirmed and remove it from the pending view.
   * Callers should pass the CommitResult so the store can record the
   * committed fact ids for subsequent correctFact flows.
   */
  markConfirmed: (batchId: string, fileId: string, commitResult: CommitResult) => void;

  /**
   * Mark a file as discarded and remove it from the pending view.
   */
  markDiscarded: (batchId: string, fileId: string) => void;

  /**
   * Committed results keyed by fileId, available for correction flows.
   * correctFact requires a committed FactRecord id from CommitResult.committedRecords.
   */
  commits: Record<string, CommitResult>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useQueueStore = create<QueueState>((set) => ({
  batches: {},
  commits: {},

  addBatch(batch) {
    set((state) => ({
      batches: { ...state.batches, [batch.batchId]: batch },
    }));
  },

  markConfirmed(batchId, fileId, commitResult) {
    set((state) => {
      const batch = state.batches[batchId];
      if (!batch) return state;

      const updatedFileResults = batch.fileResults.map((fr) =>
        fr.fileId === fileId ? { ...fr, status: 'confirmed' as const } : fr,
      );

      return {
        batches: {
          ...state.batches,
          [batchId]: { ...batch, fileResults: updatedFileResults },
        },
        commits: { ...state.commits, [fileId]: commitResult },
      };
    });
  },

  markDiscarded(batchId, fileId) {
    set((state) => {
      const batch = state.batches[batchId];
      if (!batch) return state;

      const updatedFileResults = batch.fileResults.map((fr) =>
        fr.fileId === fileId ? { ...fr, status: 'discarded' as const } : fr,
      );

      return {
        batches: {
          ...state.batches,
          [batchId]: { ...batch, fileResults: updatedFileResults },
        },
      };
    });
  },
}));

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * All pending files across all batches this session.
 * "Pending" = status === 'pending' (not yet confirmed or discarded).
 */
export function selectPendingFiles(state: QueueState): PendingFile[] {
  const result: PendingFile[] = [];
  for (const batch of Object.values(state.batches)) {
    for (const fr of batch.fileResults) {
      if (fr.status === 'pending') {
        result.push({ batchId: batch.batchId, fileResult: fr });
      }
    }
  }
  return result;
}

/**
 * Count of pending files (badge count for nav).
 */
export function selectPendingCount(state: QueueState): number {
  return selectPendingFiles(state).length;
}
